import { useMemo } from 'react';

import { useAssignMultipleBatches } from '../api';
import { SELECTION_OPTIONS } from '../constants';
import type {
  ProcessedCourseWiseBatchesData,
  ProcessedBatch,
  VariantInfo,
} from '../types';

export interface SelectedBatchDetail {
  courseName: string;
  batchId: string;
  batchName?: string;
  isWaitlist: boolean;
  batch: ProcessedBatch | null;
  scheduleDetails: Array<{
    day: string;
    time: string;
  }>;
  startDay: string;
  startDate: string;
}

interface UseConfirmModalProps {
  getSelectedBatches: () => Record<string, string>;
  processedBatchesData: ProcessedCourseWiseBatchesData;
  profileId: string; // profile.id - for waitlist API
  leadId: string; // profile.base_lead_id - for batch assignment API
  variantIds: VariantInfo[];
  country: string;
}

interface UseConfirmModalReturn {
  selectedBatchDetails: Array<SelectedBatchDetail | null>;
  isAssigning: boolean;
  handleConfirm: (selectedWaitlistReason: string) => Promise<void>;
}

export const useConfirmModal = ({
  getSelectedBatches,
  processedBatchesData,
  profileId,
  leadId,
  variantIds,
  country,
}: UseConfirmModalProps): UseConfirmModalReturn => {
  const { assignBatches, isLoading: isAssigning } = useAssignMultipleBatches();

  // Prepare data for the confirm modal
  const selectedBatchDetails = useMemo(() => {
    const selectedBatches = getSelectedBatches();
    return Object.entries(selectedBatches)
      .filter(([, batchIdOrWaitlist]) => batchIdOrWaitlist)
      .map(([courseName, batchIdOrWaitlist]) => {
        // Handle waitlist selection
        if (batchIdOrWaitlist === SELECTION_OPTIONS.WAITLIST) {
          return {
            courseName,
            batchId: SELECTION_OPTIONS.WAITLIST,
            isWaitlist: true,
            batch: null,
            scheduleDetails: [],
            startDay: '',
            startDate: '',
          };
        }

        // Find the batch details from processedBatchesData
        const courseData = processedBatchesData[courseName];
        if (!courseData) return null;

        const batch = [
          ...(courseData.recommended_batches ?? []),
          ...(courseData.other_batches ?? []),
        ].find((b: ProcessedBatch) => String(b.batch_id) == batchIdOrWaitlist);

        if (!batch) return null;

        return {
          courseName,
          batchId: batchIdOrWaitlist,
          batchName:
            typeof batch === 'object' &&
            batch &&
            'name' in batch &&
            typeof batch.name === 'string'
              ? batch.name
              : `${courseName} Batch`,
          isWaitlist: false,
          batch,
          scheduleDetails: batch.scheduleDetails as Array<{
            day: string;
            time: string;
          }>,
          startDay: String(batch.startDay),
          startDate: String(batch.startDate),
        };
      })
      .filter(Boolean);
  }, [getSelectedBatches, processedBatchesData]);

  // Handle confirm button click - assign all selected batches (including waitlist)
  const handleConfirm = async (selectedWaitlistReason: string) => {
    const batchAssignments = selectedBatchDetails
      .filter((detail): detail is NonNullable<typeof detail> => detail !== null) // Process both batch and waitlist selections
      .map(detail => {
        // Find the variant ID for this course
        const variant = variantIds.find(
          v => v.courseName === detail.courseName
        );
        return {
          profileId,
          leadId,
          variantId: variant?.variantId ?? '',
          batchId: detail.batchId,
          courseName: detail.courseName,
          country,
        };
      })
      .filter(assignment => assignment.variantId); // Only include valid assignments

    if (batchAssignments.length > 0) {
      await assignBatches(batchAssignments, selectedWaitlistReason);
    }
  };

  return {
    selectedBatchDetails,
    isAssigning,
    handleConfirm,
  };
};
